<template>
  <div 
    class="relative w-full h-full flex items-center justify-center"
    @mousemove="handleMouseMove"
    @mouseleave="resetParallax"
    ref="container"
  >
    <!-- Background Glow -->
    <div class="absolute inset-0 bg-gradient-to-tr from-primary-500/20 to-secondary-500/20 rounded-full blur-3xl animate-pulse-slow"></div>

    <svg 
      viewBox="0 0 600 500" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      class="w-full h-auto max-w-[600px] relative z-10 drop-shadow-2xl"
    >
      <!-- Definitions for Gradients and Filters -->
      <defs>
        <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:#4F46E5;stop-opacity:1" />
          <stop offset="100%" style="stop-color:#9333EA;stop-opacity:1" />
        </linearGradient>
        <linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" style="stop-color:#3B82F6;stop-opacity:1" />
          <stop offset="100%" style="stop-color:#06B6D4;stop-opacity:1" />
        </linearGradient>
        <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="5" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
      </defs>

      <!-- Connecting Lines (Background) -->
      <g class="opacity-30 stroke-gray-300" stroke-width="2" stroke-dasharray="5,5">
        <line x1="300" y1="250" x2="100" y2="150" class="animate-dash" />
        <line x1="300" y1="250" x2="500" y2="150" class="animate-dash animation-delay-1000" />
        <line x1="300" y1="250" x2="100" y2="350" class="animate-dash animation-delay-2000" />
        <line x1="300" y1="250" x2="500" y2="350" class="animate-dash animation-delay-3000" />
      </g>

      <!-- Central Core -->
      <g 
        class="transform-origin-center"
        :style="{ transform: `translate(${parallax.x * 0.5}px, ${parallax.y * 0.5}px)` }"
      >
        <!-- Outer Ring -->
        <circle cx="300" cy="250" r="80" stroke="url(#grad1)" stroke-width="4" fill="white" fill-opacity="0.9" class="animate-spin-slow" />
        <!-- Inner Ring -->
        <circle cx="300" cy="250" r="60" stroke="url(#grad2)" stroke-width="4" stroke-dasharray="20,10" fill="none" class="animate-spin-reverse-slow" />
        
        <!-- Core Logo/Image -->
        <image 
          href="@/assets/logo/logo_3x.png" 
          x="210" 
          y="200" 
          width="170" 
          height="170" 
          class="animate-pulse"
        />
        
        <circle cx="300" cy="250" r="15" fill="url(#grad2)" class="animate-ping opacity-20" />
      </g>

      <!-- Floating Elements (Orbiting) -->
      
      <!-- Top Left: Web/Code -->
      <g 
        class="animate-float"
        :style="{ transform: `translate(${parallax.x * 1.2}px, ${parallax.y * 1.2}px)` }"
      >
        <rect x="70" y="120" width="80" height="60" rx="10" fill="white" stroke="#4F46E5" stroke-width="2" filter="url(#glow)" />
        <path d="M85 150 L100 135 M85 150 L100 165" stroke="#4F46E5" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M135 150 L120 135 M135 150 L120 165" stroke="#4F46E5" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
        <line x1="105" y1="160" x2="115" y2="140" stroke="#4F46E5" stroke-width="3" stroke-linecap="round" />
      </g>

      <!-- Top Right: Cloud/Data -->
      <g 
        class="animate-float animation-delay-1000"
        :style="{ transform: `translate(${parallax.x * -0.8}px, ${parallax.y * 1.5}px)` }"
      >
        <circle cx="500" cy="150" r="40" fill="white" stroke="#06B6D4" stroke-width="2" filter="url(#glow)" />
        <path d="M485 155 A 15 15 0 0 1 500 140 A 15 15 0 0 1 515 155 A 15 15 0 0 1 500 170 A 15 15 0 0 1 485 155" fill="#06B6D4" fill-opacity="0.2" />
        <path d="M480 155 H520" stroke="#06B6D4" stroke-width="2" stroke-linecap="round" />
        <path d="M500 135 V175" stroke="#06B6D4" stroke-width="2" stroke-linecap="round" />
      </g>

      <!-- Bottom Left: Mobile -->
      <g 
        class="animate-float animation-delay-2000"
        :style="{ transform: `translate(${parallax.x * 1.5}px, ${parallax.y * -1.0}px)` }"
      >
        <rect x="80" y="320" width="40" height="70" rx="5" fill="white" stroke="#EC4899" stroke-width="2" filter="url(#glow)" />
        <rect x="90" y="330" width="20" height="40" fill="#EC4899" fill-opacity="0.1" />
        <circle cx="100" cy="380" r="3" fill="#EC4899" />
      </g>

      <!-- Bottom Right: Security/Shield -->
      <g 
        class="animate-float animation-delay-3000"
        :style="{ transform: `translate(${parallax.x * -1.2}px, ${parallax.y * -1.2}px)` }"
      >
        <path d="M500 320 L530 330 V350 C530 370 500 390 500 390 C500 390 470 370 470 350 V330 L500 320 Z" fill="white" stroke="#10B981" stroke-width="2" filter="url(#glow)" />
        <path d="M500 340 L510 350 L495 365" stroke="#10B981" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" fill="none" />
      </g>

      <!-- Floating Particles -->
      <circle cx="200" cy="200" r="4" fill="#4F46E5" class="animate-ping animation-delay-500" />
      <circle cx="400" cy="300" r="3" fill="#06B6D4" class="animate-ping animation-delay-1500" />
      <circle cx="150" cy="400" r="5" fill="#EC4899" class="animate-ping animation-delay-2500" />
      <circle cx="450" cy="100" r="4" fill="#10B981" class="animate-ping animation-delay-3500" />

    </svg>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'

const container = ref(null)
const parallax = reactive({ x: 0, y: 0 })

const handleMouseMove = (e) => {
  if (!container.value) return
  
  const rect = container.value.getBoundingClientRect()
  const x = e.clientX - rect.left - rect.width / 2
  const y = e.clientY - rect.top - rect.height / 2
  
  // Smooth damping could be added here, but direct mapping works for simple parallax
  parallax.x = x / 20 // Sensitivity factor
  parallax.y = y / 20
}

const resetParallax = () => {
  parallax.x = 0
  parallax.y = 0
}
</script>

<style scoped>
@font-face {
  font-family: 'Swash Break';
  src: url('@/assets/font/Swash Break/Swash Break.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
}

.artistic-text {
  font-family: 'Swash Break', cursive;
  font-weight: normal;
}

.transform-origin-center {
  transform-origin: center;
}

.animate-spin-slow {
  animation: spin 10s linear infinite;
}

.animate-spin-reverse-slow {
  animation: spin 15s linear infinite reverse;
}

.animate-dash {
  stroke-dasharray: 10;
  animation: dash 20s linear infinite;
}

.animate-pulse-slow {
  animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes dash {
  to { stroke-dashoffset: 1000; }
}
</style>
